import { useQuery } from '@tanstack/react-query';
import Graphic from 'components/Graphic/Graphic';
import Icon from 'components/Icon/Icon';
import QnAConvo from 'components/QnAConvo/QnAConvo';
import { askQnAsQuery } from 'pages/PatientRecords/PatientRecords';
import { ascend, isEmpty, prop, sortWith } from 'ramda';
import React from 'react';
import { Patient, QnAType } from 'types/types';
import { groupByDate } from 'utils/utils';

function ChatView({ patient, visitId }: { patient: Patient; visitId: string }) {
  const messagesWrapperRef = React.useRef<HTMLDivElement>(null);

  const { data: qnAs, isLoading } = useQuery({
    ...askQnAsQuery(visitId),
    enabled: !!visitId,
  });

  const { groupedQnAs } = React.useMemo(() => {
    const QnAs = (qnAs || []) as QnAType[];
    if (!QnAs) return {};

    return {
      groupedQnAs: groupByDate(sortWith([ascend(prop('createdAt') as any)], QnAs)) as Record<
        string,
        QnAType[]
      >,
    };
  }, [qnAs]);

  return (
    <div className="flex flex-col pt-0 pb-0 tablet:pt-0 tablet:pb-7">
      <div className="rounded-[15px] h-[600px] border border-zinc-200 flex flex-col transition-colors duration-300 bg-white">
        {isLoading ? (
          <div className="flex flex-col w-full h-full justify-center overflow-hidden">
            <div className="flex flex-col w-fit h-full mx-auto justify-center overflow-hidden">
              <Icon id="loader" className="w-10 h-10 text-gray-300 mx-auto" />
              <span>Loading conversation with {patient.firstName || 'patient'}...</span>
            </div>
          </div>
        ) : !groupedQnAs || isEmpty(groupedQnAs) ? (
          <div className="flex flex-col w-full h-full justify-center items-center overflow-hidden">
            <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
            <div className="text-center mt-3">
              <p className="font-medium">Patient has no chat history (messages)!</p>
              <p className="text-neutral-500 text-xs">
                {`See all the complete chat history you've had with the patient`}
              </p>
            </div>
          </div>
        ) : (
          <div
            ref={messagesWrapperRef}
            id="message__scroller"
            className="flex-col pb-5 pt-5 px-5 tablet:px-8 overflow-y-auto relative"
          >
            <QnAConvo qnAs={groupedQnAs} patient={patient} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatView;
